import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as yup from "yup";
import { Formik, FormikProps } from 'formik';
import { notification, Pagination } from 'antd';
import CustomTable from '../../components/common/Table.tsx';
import Searchbar from '../../components/common/Searchbar.tsx';
import SideSheet from '../../components/common/Sidesheet.tsx';
import Confirmation from '../../components/common/Confirmation.tsx';
import { capitalizeFirstLetter } from '../../utils/util.service.ts';
import { setBreadcrumbs } from '../../slices/BreadcrumbSlice.ts';
import { AppDispatch, RootState } from '../../store/store';
import { IClientReq, IClientResponse } from '../../type/type.ts';
import ClientForm from '../../components/sheet/ClientForm.tsx';
import {
  createClient, getAllClients, fetchClient, updateClient, deleteClient,
  setOffset, setLimit, setSortField, setSortOrder,
} from '../../slices/ClientSlice.ts';

const Client: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [isSideSheetOpen, setSideSheetOpen] = useState(false);
  const { clients, loading, dataCount, offset, limit, getClientbyIdvalue } = useSelector((state: RootState) => state.clientSlice);
  const [selectedClient, setSelectedClient] = useState<number | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  const [confirmationMessage, setConfirmationMessage] = useState("Do you want to submit the form?");
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<IClientResponse | null>(null);


  const [sortConfig, setSortConfig] = useState<{
    key: string;
    order: "ascend" | "descend" | null;
  }>({
    key: "",
    order: null,
  });


  const initialValues: IClientReq = {
    firstname: "",
    lastname: "",
    email: "",
    mobile: "",
    age: 0,
    status: "",
    TFN_number: "",
    license: "",
    proofage: "",
    passport: "",
    city: "",
    state: "",
    address: "",
    zipcode: 0,
    latitude: "",
    longitude: ""
  };

  const showNotification = (type: "success" | "error", message: string) => {
    notification[type]({
      message,
      placement: "top",
    });
  };
  const handleSearch = (value: string) => {
    setQuery(value);
    dispatch(setOffset(0));
  };

  const handlePaginationChange = (page: number, newPageSize?: number) => {

    dispatch(setOffset(page - 1));
    if (newPageSize && newPageSize !== limit) {
      dispatch(setLimit(newPageSize));
    }
  };

  const handleEditClick = async (client: IClientResponse) => {
    try {
      setSelectedClient(client.id);
      setSideSheetOpen(true);
      const response = await dispatch(fetchClient({ clientId: client.id })).unwrap();

      if (formikRef.current && response.data) {
        const clientData = response.data;
        formikRef.current.setValues({
          firstname: clientData.name?.split(' ')[0] || '',
          lastname: clientData.name?.split(' ')[1] || '',
          email: clientData.email || '',
          mobile: clientData.mobile || '',
          age: clientData.age || 0,
          status: clientData.status || '',
          TFN_number: clientData.TFN_number || '',
          license: clientData.license || '',
          proofage: clientData.proof_of_age || '',
          passport: clientData.passport || '',
          city: clientData.city || '',
          state: clientData.state || '',
          address: clientData.address || '',
          zipcode: parseInt(clientData.pincode) || 0,
          latitude: clientData.latitude || '',
          longitude: clientData.longitude || '',
        });
      }
    } catch (error: any) {
      showNotification("error", "Failed to fetch client data");
      setSideSheetOpen(false);
      setSelectedClient(null);
    }
  };

  const handleSortChange = (_: any, __: any, sorter: any) => {
    if (Array.isArray(sorter)) {
      sorter = sorter[0];
    }

    const { field, order } = sorter || {};

    const sortOrder = order === 'ascend' ? 1 : -1;

    setSortConfig({
      key: field || null,
      order: order || null
    });

    dispatch(setSortField(field));
    dispatch(setSortOrder(sortOrder));

    dispatch(setOffset(offset));
  };

  useEffect(() => {
    if (getClientbyIdvalue?.data && selectedClient && formikRef.current) {
      const clientData = getClientbyIdvalue.data;
      const currentValues = formikRef.current.values;
      const newValues = {
        firstname: clientData.name?.split(' ')[0] || '',
        lastname: clientData.name?.split(' ')[1] || '',
        email: clientData.email || '',
        mobile: clientData.mobile || '',
        age: clientData.age || 0,
        status: clientData.status || '',
        TFN_number: clientData.TFN_number || '',
        license: clientData.license || '',
        proofage: clientData.proof_of_age || '',
        passport: clientData.passport || '',
        city: clientData.city || '',
        state: clientData.state || '',
        address: clientData.address || '',
        zipcode: parseInt(clientData.pincode) || 0,
        latitude: clientData.latitude || '',
        longitude: clientData.longitude || '',
      };

      const hasChanges = Object.keys(newValues).some(key =>
        newValues[key as keyof typeof newValues] !== currentValues[key as keyof typeof currentValues]
      );

      if (hasChanges) {
        formikRef.current.setValues(newValues);
      }
    }
  }, [selectedClient, getClientbyIdvalue]);

  useEffect(() => {
    dispatch(getAllClients({
      offset: offset,
      limit: limit,
      sortField: sortConfig.key ?? null,
      sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null,
      searchInput: query
    }));
  }, [dispatch, offset, limit, sortConfig, query]);

  useEffect(() => {
    if (location.state?.editMode && location.state?.userData) {
      const userData = location.state.userData;

      setSideSheetOpen(true);

      setTimeout(() => {
        if (formikRef.current) {
          formikRef.current.setValues({
            firstname: userData.firstname || '',
            lastname: userData.lastname || '',
            email: userData.email || '',
            mobile: userData.mobile || '',
            age: userData.age || 0,
            status: userData.status || '',
            TFN_number: userData.TFN_number || '',
            license: userData.license || '',
            proofage: userData.proofage || '',
            passport: userData.passport || '',
            city: userData.city || '',
            state: userData.state || '',
            address: userData.address || '',
            zipcode: +userData.zipcode || 0,
            latitude: userData.latitude || '',
            longitude: userData.longitude || '',
          });
        }
      }, 0);
    }
  }, [location.state]);


  useEffect(() => {
    dispatch(setBreadcrumbs([
      {
        title: "Client Management",
        path: '/client'
      }
    ]));
  }, []);

  const handleDeleteClick = (client: IClientResponse) => {
    setClientToDelete(client);
    setConfirmationMessage("Are you sure you want to delete this client?");
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;

    try {
      await dispatch(deleteClient(clientToDelete.id)).unwrap();
      showNotification("success", "Client deleted successfully");
      dispatch(getAllClients({
        searchInput: query,
        offset: offset,
        limit: limit,
        sortField: sortConfig.key ?? null,
        sortOrder: sortConfig.order && sortConfig.order === 'ascend' ? 1 : sortConfig.order === 'descend' ? -1 : null
      }));
    } catch (error: any) {
      showNotification("error", error.message || "Failed to delete client");
    } finally {
      setIsDeleteConfirmationOpen(false);
      setClientToDelete(null);
    }
  };

  const columns = [
    {
      title: <span className="text-[#6D6D6D]">S.No</span>,
      dataIndex: 'id',
      key: 'id',
      render: (_: any, __: any, index: number) =>
        (offset * limit + index + 1).toString().padStart(2, "0"),
      width: 80,
    },
    {
      title: <span className="text-[#6D6D6D]">Name</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "name" ? sortConfig.order : null,
      render: (name: string) => <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(name)}</span>,
    },
    {
      title: <span className="text-[#6D6D6D]">Phone</span>,
      dataIndex: 'mobile',
      key: 'mobile',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "mobile" ? sortConfig.order : null,
      render: (mobile: string | null) =>
        mobile ? <span className="text-[#0A0A0A] font-medium text-sm">{capitalizeFirstLetter(mobile)}</span> : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Email</span>,
      dataIndex: 'email',
      key: 'email',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "email" ? sortConfig.order : null,
      render: (_: any, record: IClientResponse) =>
        record.email
          ? <span className="text-[#0A0A0A] font-medium text-sm">{record.email}</span>
          : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Age</span>,
      dataIndex: 'age',
      key: 'age',
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: sortConfig.key === "age" ? sortConfig.order : null,
      render: (_: any, record: IClientResponse) =>
        record.age
          ? <span className="text-[#0A0A0A] font-medium text-sm">{record.age}</span>
          : '-'
    },
    {
      title: <span className="text-[#6D6D6D]">Action</span>,
      dataIndex: 'action',
      key: 'action',
      sorter: false,
      render: (_: any, record: IClientResponse) => (
        <div className="flex items-center gap-3">
          <img
            src="/client/edit.svg"
            alt="Edit"
            aria-label="Edit Client"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleEditClick(record)}
          />
          <img
            src="/client/delete.svg"
            alt="Delete"
            aria-label="Delete Client"
            className="w-5 h-5 cursor-pointer hover:scale-105 transition-transform"
            onClick={() => handleDeleteClick(record)}
          />
        </div>
      )
    }

  ];

  const clientSchema = yup.object().shape({
    firstname: yup.string()
      .required("First name is required")
      .min(2, "First name must be at least 2 characters")
      .max(30, "First name must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "First name can only contain letters and spaces"),

    lastname: yup.string()
      .required("Last name is required")
      .min(2, "Last name must be at least 2 characters")
      .max(30, "Last name must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "Last name can only contain letters and spaces"),

    email: yup.string()
      .email("Invalid email")
      .required("Email is required")
      .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Invalid email format")
      .max(50, "Email must be less than 50 characters"),

    mobile: yup.string()
      .required("Mobile number is required")
      .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits"),

    age: yup.number()
      .required("Age is required")
      .min(1, "Age must be greater than 0")
      .max(120, "Age must be less than 120"),

    status: yup.string()
      .required("Status is required"),

    TFN_number: yup.string()
      .required("TFN document is required"),

    license: yup.string()
      .required("License document is required"),

    proofage: yup.string()
      .required("Proof of age document is required"),

    passport: yup.string()
      .required("Passport document is required"),

    city: yup.string()
      .required("City is required")
      .min(3, "City must be at least 3 characters")
      .max(30, "City must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "City can only contain letters and spaces"),

    state: yup.string()
      .required("State is required")
      .min(3, "State must be at least 3 characters")
      .max(30, "State must be less than 30 characters")
      .matches(/^[a-zA-Z\s]+$/, "State can only contain letters and spaces"),

    address: yup.string()
      .required("Address is required")
      .min(5, "Address must be at least 5 characters")
      .max(255, "Address must be less than 255 characters"),

    zipcode: yup.number()
      .required("Zipcode is required")
      .typeError("Zipcode must be a number")
      .min(100000, "Zipcode must be 6 digits")
      .max(999999, "Zipcode must be 6 digits"),

    latitude: yup.string()
      .required("Latitude is required")
      .matches(/^-?([1-8]?[0-9](\.[0-9]+)?|90(\.0+)?)$/, "Invalid latitude format"),

    longitude: yup.string()
      .required("Longitude is required")
      .matches(/^-?((1?[0-7]?|[0-9]?)[0-9](\.[0-9]+)?|180(\.0+)?)$/, "Invalid longitude format")
  });


  const formikRef = useRef<FormikProps<IClientReq>>(null);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={clientSchema}
      enableReinitialize={true}
      onSubmit={async (values, { resetForm }) => {
        const data: IClientReq = {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          mobile: values.mobile,
          age: values.age,
          status: values.status,
          TFN_number: values.TFN_number || "",
          license: values.license || "",
          proofage: values.proofage || "",
          passport: values.passport || "",
          city: values.city || "",
          state: values.state || "",
          address: values.address || "",
          zipcode: values.zipcode || 0,
          latitude: values.latitude || "",
          longitude: values.longitude || "",
        };

        setConfirmationMessage(selectedClient ? "Do you want to update the client?" : "Do you want to create a new client?");
        setIsConfirmationOpen(true);
        setConfirmAction(() => async () => {
          try {
            setSideSheetOpen(false);

            try {
              if (selectedClient) {
                await dispatch(updateClient({
                  ...data,
                  id: selectedClient
                })).unwrap();
                showNotification("success", "Client updated successfully");
              } else {
                await dispatch(createClient({
                  ...data,
                  rejectValue: ''
                })).unwrap();
                showNotification("success", "Client created successfully");
              }
              resetForm();
            } catch (error: any) {
              showNotification("error", error.message || "Operation failed");
              resetForm();
            }

            await dispatch(getAllClients({
              offset: offset,
              limit: limit,
              sortField: "",
              sortOrder: null,
              searchInput: query,
            }));

            resetForm();
            setSideSheetOpen(false);
            setSelectedClient(null);
          } catch (error: any) {
            showNotification("error", error.message || "Operation failed");
          }
          setIsConfirmationOpen(false);
        });
      }}

    >
      {({ handleSubmit, values, touched, errors, setFieldValue, handleChange, resetForm, setFieldTouched, validateField }) => (
        <>
          <div className="bg-[#EDF1F6] h-[76px] mt-[10px] flex justify-between items-center px-[20px]">
            <p className="font-Montserrat font-semibold text-[20px] leading-[24.38px] text-[#0A0A0A] ">Client Management</p>
            <div className="flex justify-end gap-3 ">
              <div className="flex justify-end ">
                <Searchbar search={query} onSearch={handleSearch} />
              </div>
              <SideSheet
                btnLabel={selectedClient ? "Edit Client" : "Add New Client"}
                width={800}
                title={selectedClient ? "Edit Client" : "Add New Client"}
                isOpen={isSideSheetOpen}
                setOpen={setSideSheetOpen}
                onCancel={() => {
                  setSideSheetOpen(false);
                  resetForm();
                  setSelectedClient(null);
                }}
                onSubmit={handleSubmit}
                submitLabel={selectedClient ? "Update" : "Submit"}
              >
                <ClientForm
                  handleChange={handleChange}
                  values={values}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                />
              </SideSheet>
            </div>
          </div>
          <div>
            <CustomTable
              columns={columns}
              dataSource={clients}
              loading={loading}
              onChange={handleSortChange}
              scrollHeight={800}
            />
          </div>
          <div className=" sticky bottom-0 flex justify-end py-4">
            <Pagination
              total={dataCount}
              showSizeChanger={false}
              onChange={handlePaginationChange}
            />
          </div>
          <Confirmation
            label="Confirmation"
            message={confirmationMessage}
            isOpen={isConfirmationOpen || isDeleteConfirmationOpen}
            onClose={() => {
              setIsConfirmationOpen(false);
              setIsDeleteConfirmationOpen(false);
              setClientToDelete(null);
            }}
            onConfirm={isDeleteConfirmationOpen ? handleDeleteConfirm : (confirmAction || (() => { }))}
            confirmButtonLabel="Confirm"
          />
        </>

      )}
    </Formik>
  );
};
export default Client;